<script>
  // ══════════════════════════════════════════════════════════════════════════
  // БОЕВОЙ ПРОПУСК — перенос pass.tsx 1:1 (версия с настоящими фото наград и
  // компактной дорожкой): «Дорожка» · «Премиум» · «Купить опыт» · «Задания».
  // Сезон и тиры — из общего реестра battlepassdef.js (он же у сервера), по
  // сети гоняется только прогресс игрока; серверные tiers перекрывают локальные.
  // ══════════════════════════════════════════════════════════════════════════
  import { t } from '../../i18n.js';
  import { num, cc } from '../partsfn.js';
  import { SEASONS, activeSeason, spCumulative } from '../battlepassdef.js';
  import Ico from '../ico.svelte';
  import Empty from '../empty.svelte';
  import Scroll from '../ui/scroll.svelte';
  import Sechead from '../ui/sechead.svelte';
  import Panel from '../ui/panel.svelte';
  import Prog from '../ui/prog.svelte';
  import Chip from '../ui/chip.svelte';
  import GoldBtn from '../ui/goldbtn.svelte';
  import GhostBtn from '../ui/ghostbtn.svelte';
  import SlotTabs from '../ui/slottabs.svelte';
  import Bullet from '../ui/bullet.svelte';
  import BuyBtn from '../ui/buybtn.svelte';
  import Jc from '../ui/jc.svelte';
  import Cell from './passcell.svelte';

  export let bp = null;
  export let balance = 0;
  export let nui = () => {};

  /** Сколько уровней показываем на одной странице дорожки. */
  const PAGE = 8;
  /** Пакеты уровней — те же три позиции, что были в старом «Купить опыт». */
  const LEVEL_PACKS = [1, 5, 25];
  const KIND_ICO = {
    job: 'skills', explore: 'map', social: 'users', theme: 'battlepass',
    faction: 'shield', economy: 'coins', world: 'world',
  };

  let tab = 'track';
  const season = activeSeason(Date.now()) ?? SEASONS['s1_paleto_gold'] ?? null;

  $: level = bp?.level ?? 1;
  $: spThis = bp?.spThisLevel ?? 0;
  $: spNext = bp?.spForNext ?? 0;
  $: pct = spNext > 0 ? Math.round((spThis / spNext) * 100) : 0;
  $: daysLeft = bp?.endsAt ? Math.max(0, Math.ceil((bp.endsAt - Date.now()) / 86400000)) : 0;
  $: premium = !!bp?.premium;
  $: premPrice = bp?.premiumPriceJC ?? season?.premiumPriceJC ?? 950;
  $: claimedFree = new Set(bp?.claimedFree ?? []);
  $: claimedPrem = new Set(bp?.claimedPrem ?? []);

  // Дорожка — то, что реально выдаёт сервер; локальный сезон — запасной вариант.
  $: tiers = (() => {
    if (!bp?.tiers?.length) return season?.tiers ?? [];
    return bp.tiers.map((ti) => ({
      level: ti.level,
      spReq: bp.spForNext ?? 50,
      free: ti.free ? { kind: 'qol', unlock: 'bp_free_' + ti.level } : undefined,
      freeLabel: ti.free?.title,
      premium: { kind: 'qol', unlock: 'bp_prem_' + ti.level },
      premiumLabel: ti.premium?.title ?? '—',
      rarity: ti.level % 10 === 0 ? 'epic' : ti.premium ? 'rare' : 'common',
      milestone: ti.level % 10 === 0,
    }));
  })();
  $: maxPage = Math.max(0, Math.ceil(tiers.length / PAGE) - 1);

  // Открываемся на странице текущего уровня (и возвращаемся к ней при смене
  // уровня) — как старое меню, чтобы не листать вручную.
  let page = 0;
  let _pageKey = null;
  $: {
    const k = level + ':' + maxPage;
    if (k !== _pageKey) { _pageKey = k; page = Math.min(maxPage, Math.floor((level - 1) / PAGE)); }
  }
  $: shown = tiers.slice(page * PAGE, page * PAGE + PAGE);

  /** Сколько наград доступно, но не забрано — для кнопки «Забрать всё». */
  $: ready = tiers.reduce((n, ti) => {
    if (ti.level > level) return n;
    let k = 0;
    if (ti.free && !claimedFree.has(ti.level)) k++;
    if (premium && ti.premium && !claimedPrem.has(ti.level)) k++;
    return n + k;
  }, 0);

  const claim = (lvl, track) => nui('bpClaim', { level: lvl, track });

  // серверные награды по уровню — источник настоящих фото и подписей
  $: rewardMap = (() => {
    const m = new Map();
    bp?.tiers?.forEach((ti) => m.set(ti.level, ti));
    return m;
  })();

  $: week = bp?.week ?? 1;
  $: challenges = season ? season.challenges.filter((c) => c.week <= week) : [];

  // ── Задания сезона (TasksTab)
  let kind = 'day';
  $: daily = bp?.daily ?? [];
  $: weekly = bp?.weekly ?? [];
  $: tlist = kind === 'day' ? daily : kind === 'week' ? weekly : [];
  $: seasonWeeks = [...new Set(challenges.map((c) => c.week))].sort((a, b) => b - a);
</script>

<div class="mn-fill">
  <div class="head">
    <Sechead kicker={`${t('m.bp.kicker')}${season ? ' · ' + (bp?.name ?? season.name) : ''}`} title={t('m.bp.title')}>
      <svelte:fragment slot="meta">
        {#if daysLeft > 0}<span class="mn-ico-txt"><Ico n="hourglass" s={15} />{t('m.bp.daysLeft', { n: daysLeft })}</span>{/if}
      </svelte:fragment>
    </Sechead>

    <!-- Шапка прогресса — одна на все вкладки: уровень, полоса SP, статус премиума. -->
    <Panel>
      <div class="mn-bp-head">
        <div class="lvbox">
          <div class="n mn-num">{level}</div>
          <div class="c">{t('m.ov.level')}</div>
        </div>
        <div class="prog">
          <div class="ui-field" style="margin-bottom:0">
            <div class="labrow">
              <span class="lab" style="margin-bottom:0">{season?.tagline ?? t('m.bp.kicker')}</span>
              <span class="note mn-num">{num(spThis)} / {num(spNext)} SP</span>
            </div>
            <Prog large value={pct} />
          </div>
          <div class="ui-row" style="margin-top:12px;align-items:center">
            {#if premium}
              <Chip tone="pos"><Ico n="check" s={14} />{t('m.bp.premOn')}</Chip>
            {:else}
              <GoldBtn size="sm" on:click={() => (tab = 'prem')}>
                <Ico n="premium" s={15} />{t('m.bp.buyPrem')} · <Jc v={premPrice} />
              </GoldBtn>
            {/if}
            {#if ready > 0}
              <GhostBtn size="sm" on:click={() => nui('bpClaimAll')}>
                <Ico n="gift" s={15} />{t('m.bp.takeAll')} · {ready}
              </GhostBtn>
            {/if}
            <span class="ui-grow"></span>
            <span class="mn-ico-txt mn-num" style="color:var(--gold);font:800 13px var(--f-head)">
              <Ico n="currency" s={16} />{cc(balance)}
            </span>
          </div>
        </div>
      </div>
    </Panel>

    <div style="margin-top:var(--mn-gap)">
      <SlotTabs
        items={[
          { id: 'track', label: t('m.bp.track'), icon: 'list' },
          { id: 'prem', label: t('m.bp.prem'), icon: 'premium' },
          { id: 'levels', label: t('m.bp.buyExp'), icon: 'bolt' },
          { id: 'tasks', label: t('m.bp.tasks'), icon: 'quests', n: challenges.length },
        ]}
        active={tab} onChange={(id) => (tab = id)}
      />
    </div>
  </div>

  <div class="body">
    {#if tab === 'track'}
      {#if shown.length === 0}
        <Panel><Empty icon="battlepass" text={t('m.empty')} /></Panel>
      {:else}
        <Scroll>
          <!-- Листалка: страница по 8 уровней, как в оригинале (там было 6). -->
          <div class="mn-tools">
            <GhostBtn size="sm" disabled={page === 0} on:click={() => (page = Math.max(0, page - 1))}>
              <Ico n="left" s={15} />
            </GhostBtn>
            <span class="mn-num" style="font:800 12px var(--f-head);color:var(--ink-soft)">
              {t('m.bp.levels', { a: page * PAGE + 1, b: Math.min(tiers.length, (page + 1) * PAGE) })}
            </span>
            <GhostBtn size="sm" disabled={page >= maxPage} on:click={() => (page = Math.min(maxPage, page + 1))}>
              <Ico n="right" s={15} />
            </GhostBtn>
            <GhostBtn size="sm" on:click={() => (page = Math.min(maxPage, Math.floor((level - 1) / PAGE)))}>
              <Ico n="target" s={15} />{t('m.bp.toCurrent')}
            </GhostBtn>
            <span class="ui-grow"></span>
            <Chip tone="gold"><Ico n="premium" s={14} />{t('m.bp.prem')}</Chip>
            <Chip><Ico n="dot" s={14} />{t('m.bp.free')}</Chip>
          </div>

          <div class="mn-bp">
            <!-- премиум-линия -->
            <div class="mn-bp-lane prem">
              <div class="tag"><Ico n="premium" s={15} />{t('m.bp.prem')}</div>
              <div class="mn-bp-rail">
                {#each shown as ti ('p' + ti.level)}
                  <div class="mn-bp-col">
                    <Cell
                      tier={ti} track="premium" reached={ti.level <= level}
                      claimed={claimedPrem.has(ti.level)} {premium}
                      reward={rewardMap.get(ti.level)?.premium}
                      onClaim={() => claim(ti.level, 'premium')}
                    />
                  </div>
                {/each}
              </div>
            </div>

            <!-- хребет: номера уровней между линиями -->
            <div class="mn-bp-spine">
              <div class="tag"></div>
              <div class="mn-bp-rail">
                {#each shown as ti ('l' + ti.level)}
                  <div class="mn-bp-col">
                    <div class={'mn-lv' + (ti.level === level ? ' now' : '') + (ti.milestone ? ' mile' : '') + (ti.level <= level ? ' reached' : '')}>
                      {#if ti.milestone}<Ico n="star" s={13} />{/if}
                      {ti.level}
                    </div>
                  </div>
                {/each}
              </div>
            </div>

            <!-- бесплатная линия -->
            <div class="mn-bp-lane">
              <div class="tag"><Ico n="dot" s={15} />{t('m.bp.free')}</div>
              <div class="mn-bp-rail">
                {#each shown as ti ('f' + ti.level)}
                  <div class="mn-bp-col">
                    <Cell
                      tier={ti} track="free" reached={ti.level <= level}
                      claimed={claimedFree.has(ti.level)} premium
                      reward={rewardMap.get(ti.level)?.free}
                      onClaim={() => claim(ti.level, 'free')}
                    />
                  </div>
                {/each}
              </div>
            </div>
          </div>

          <!-- нижний блок метрик убран: дублировал шапку и заставлял листать дорожку -->
        </Scroll>
      {/if}

    {:else if tab === 'prem'}
      <Scroll>
        <div class="hint" style="margin-bottom:var(--mn-gap)">{t('m.bp.premAbout')}</div>
        <div class="mn-prem">
          <!-- что есть сейчас -->
          <div class="card">
            <Sechead title={t('m.bp.starterPass')} divider={false}>
              <svelte:fragment slot="meta">{#if !premium}<Chip tone="pos">{t('m.bp.yours')}</Chip>{/if}</svelte:fragment>
            </Sechead>
            <Bullet>{t('m.bp.free1')}</Bullet>
            <Bullet>{t('m.bp.free2')}</Bullet>
            <Bullet>{t('m.bp.free3')}</Bullet>
            <div class="mn-buy">
              <GhostBtn block size="sm" disabled style="opacity:0.5;cursor:default">
                {premium ? t('m.bp.included') : t('m.bp.yours')}
              </GhostBtn>
            </div>
          </div>

          <!-- премиум -->
          <div class="card gold">
            <Sechead title={t('m.bp.premPass')} divider={false}>
              <svelte:fragment slot="meta">{#if premium}<Chip tone="pos"><Ico n="check" s={14} />{t('m.bp.premOn')}</Chip>{/if}</svelte:fragment>
            </Sechead>
            <Bullet>{t('m.bp.prem1', { n: tiers.length })}</Bullet>
            <Bullet>{t('m.bp.prem2')}</Bullet>
            <Bullet>{t('m.bp.prem3')}</Bullet>
            <Bullet>{t('m.bp.prem4')}</Bullet>
            <div class="mn-buy">
              {#if premium}
                <GhostBtn block size="sm" disabled style="opacity:0.5;cursor:default">{t('m.bp.premOn')}</GhostBtn>
              {:else}
                <BuyBtn price={premPrice} {balance} onBuy={() => nui('bpPremium')} />
              {/if}
            </div>
          </div>

          <!-- бандл -->
          <div class="card">
            <Sechead title={t('m.bp.bundle')} divider={false} />
            <Bullet>{t('m.bp.bundle1')}</Bullet>
            <Bullet>{t('m.bp.bundle2')}</Bullet>
            <Bullet>{t('m.bp.bundle3')}</Bullet>
            <div class="mn-buy">
              <BuyBtn price={season?.premiumPlusPriceJC ?? premPrice * 2} {balance} onBuy={() => nui('bpPremiumPlus')} />
            </div>
          </div>
        </div>
      </Scroll>

    {:else if tab === 'levels'}
      <Scroll>
        <div class="hint" style="margin-bottom:var(--mn-gap)">{t('m.bp.buyExpAbout')}</div>
        <div class="mn-grid wide">
          {#each LEVEL_PACKS as lv (lv)}
            {@const price = (season?.extraLevelPriceJC ?? 90) * lv}
            {@const sp = spCumulative(Math.min(tiers.length, level + lv)) - spCumulative(level)}
            <Panel>
              <div style="text-align:center">
                <div style="font:800 40px var(--f-head);color:var(--gold);font-variant-numeric:tabular-nums;line-height:1">+{lv}</div>
                <div class="sub" style="margin-top:6px;font:700 10px var(--f-body);letter-spacing:0.18em;text-transform:uppercase;color:var(--ink-mute)">
                  {t('m.bp.levelsWord')}
                </div>
              </div>
              <div style="margin-top:var(--mn-gap)">
                <Bullet>{t('m.bp.toLevel', { n: Math.min(tiers.length, level + lv) })}</Bullet>
                <Bullet>{t('m.bp.spGain', { n: num(sp) })}</Bullet>
                <Bullet>{t('m.bp.unlockRewards', { n: lv * (premium ? 2 : 1) })}</Bullet>
              </div>
              <div class="mn-buy">
                <BuyBtn {price} {balance} onBuy={() => nui('bpBuyLevels', { levels: lv })} />
              </div>
            </Panel>
          {/each}
        </div>
      </Scroll>

    {:else}
      <!-- Задания сезона (перенос TasksTab) -->
      <Scroll>
        <SlotTabs
          items={[
            { id: 'day', label: t('m.bp.daily'), icon: 'dailies', n: daily.length },
            { id: 'week', label: t('m.bp.weekly'), icon: 'calendar', n: weekly.length },
            { id: 'season', label: t('m.bp.season'), icon: 'battlepass', n: challenges.length },
          ]}
          active={kind} onChange={(id) => (kind = id)}
        />

        {#if kind !== 'season'}
          {#if tlist.length === 0}
            <Panel><Empty icon="quests" text={t('m.bp.noTasks')} /></Panel>
          {:else}
            <div class="mn-grid wide">
              {#each tlist as task, i (i)}
                {@const done = (task.exp ?? 0) < 0 || (task.count ?? 0) >= (task.maxCount ?? 1)}
                {@const p = Math.min(100, Math.round(((task.count ?? 0) / Math.max(1, task.maxCount ?? 1)) * 100))}
                <Panel style={done ? 'opacity:0.62' : undefined}>
                  <div class="ui-row" style="align-items:flex-start">
                    <Ico n={done ? 'ok' : 'quests'} s={18} style="color:{done ? 'var(--pos)' : 'var(--gold)'};margin-top:2px" />
                    <div class="ui-grow" style="font:700 13px var(--f-body);line-height:1.4">{task.name}</div>
                  </div>
                  <div class="ui-field" style="margin-top:12px;margin-bottom:0">
                    <div class="labrow">
                      <span class="lab" style="margin-bottom:0">{done ? t('m.bp.done') : t('m.bp.progress')}</span>
                      <span class="note mn-num">{num(task.count)} / {num(task.maxCount ?? 1)}</span>
                    </div>
                    <Prog value={done ? 100 : p} />
                  </div>
                  <div class="mn-buy">
                    <!-- Сервер помечает выполненное задание exp = -1 — показываем
                         «Выполнено», а не бессмысленное «+1 SP». -->
                    {#if done}<Chip tone="pos"><Ico n="check" s={13} />{t('m.bp.done')}</Chip>
                    {:else}<Chip tone="gold"><Ico n="bolt" s={13} />+{num(task.exp ?? 0)} SP</Chip>{/if}
                  </div>
                </Panel>
              {/each}
            </div>
          {/if}
        {:else}
          {#if challenges.length === 0}
            <Panel><Empty icon="battlepass" text={t('m.bp.noTasks')} /></Panel>
          {:else}
            {#each seasonWeeks as w (w)}
              <div style="margin-bottom:var(--mn-gap)">
                <Sechead title={t('m.bp.weekN', { n: w })} centerLine>
                  <svelte:fragment slot="meta">{#if w === week}<Chip tone="gold">{t('m.bp.now')}</Chip>{/if}</svelte:fragment>
                </Sechead>
                <div class="mn-grid wide">
                  {#each challenges.filter((c) => c.week === w) as c (c.id)}
                    <Panel style={c.premium && !premium ? 'opacity:0.6' : undefined}>
                      <div class="ui-row" style="align-items:flex-start">
                        <Ico n={KIND_ICO[c.kind] || 'quests'} s={18} style="color:var(--gold);margin-top:2px" />
                        <div class="ui-grow" style="font:700 13px var(--f-body);line-height:1.4">{c.text}</div>
                      </div>
                      <div class="mn-buy">
                        <div class="ui-row" style="align-items:center">
                          <Chip tone="gold"><Ico n="bolt" s={13} />+{num(c.sp)} SP</Chip>
                          {#if c.goal}<Chip><Ico n="target" s={13} />×{c.goal}</Chip>{/if}
                          {#if c.premium}<Chip tone={premium ? 'pos' : 'default'}><Ico n="premium" s={13} />{t('m.bp.prem')}</Chip>{/if}
                        </div>
                      </div>
                    </Panel>
                  {/each}
                </div>
              </div>
            {/each}
          {/if}
        {/if}
      </Scroll>
    {/if}
  </div>
</div>
